import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Button,
  Checkbox,
  Select,
  Separator,
  Tab,
  TabBody,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Tabs,
  TextInput,
  Toolbar,
  Window,
  WindowContent,
  WindowHeader,
  ProgressBar,
} from 'react95';
import Draggable from 'react-draggable';
import { t } from './i18n';
import WindowQuote from './WindowQuote';

const LAPTOP_COLUMNS = [
  ['type', '', true],
  ['brand', 'shopBrand', true],
  ['model', 'shopModel', true],
  ['grade', 'shopGrade', true],
  ['cpu', 'shopCpu', true],
  ['ram', 'shopRam', true],
  ['storage', 'shopStorage', true],
  ['battery', 'shopBattery', true],
  ['price_ttc', 'shopPrice', true],
  ['color', 'shopColor', false],
  ['gpu', 'shopGpu', false],
  ['os', 'shopOs', false],
];

const SMARTPHONE_COLUMNS = [
  ['brand', 'shopBrand', true],
  ['model', 'shopModel', true],
  ['storage', 'shopStorage', true],
  ['color', 'shopColor', true],
  ['grade', 'shopGrade', true],
  ['price_ttc', 'shopPrice', true],
];

const LAPTOP_FILTER_FIELDS = ['brand', 'ram', 'storage', 'grade', 'color', 'cpu', 'battery', 'gpu', 'os'];
const SMARTPHONE_FILTER_FIELDS = ['brand', 'storage', 'grade', 'color'];

function WindowShop({ close, centerWindows, zIndex, isFocused, onFocus, lang }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState({});
  const [sortField, setSortField] = useState('price_ttc');
  const [sortDir, setSortDir] = useState('asc');
  const [showQuote, setShowQuote] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0 laptops, 1 smartphones
  const [openMenu, setOpenMenu] = useState(null); // 'filters' | 'columns' | null
  const [visibleLaptopCols, setVisibleLaptopCols] = useState(() =>
    new Set(LAPTOP_COLUMNS.filter(c => c[2]).map(c => c[0]))
  );
  const [visiblePhoneCols, setVisiblePhoneCols] = useState(() =>
    new Set(SMARTPHONE_COLUMNS.filter(c => c[2]).map(c => c[0]))
  );
  const [laptopFilters, setLaptopFilters] = useState({
    name: '',
    brand: 'all',
    ram: 'all',
    storage: 'all',
    grade: 'all',
    color: 'all',
    cpu: 'all',
    battery: 'all',
    gpu: 'all',
    os: 'all',
  });
  const [phoneFilters, setPhoneFilters] = useState({
    name: '',
    brand: 'all',
    storage: 'all',
    grade: 'all',
    color: 'all',
  });
  const menuRef = useRef(null);

  const doCenter = useCallback(() => {
    centerWindows();
    setTimeout(() => centerWindows(), 50);
  }, [centerWindows]);

  useEffect(() => {
    doCenter();
    setTimeout(() => {
      doCenter();
      setOpacity(1);
    }, 150);
    fetchCatalog();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const fetchCatalog = () => {
    setLoading(true);
    setError(false);
    fetch('shop/api.php')
      .then(r => {
        if (!r.ok) throw new Error('Network error');
        return r.json();
      })
      .then(data => {
        setCatalog(Array.isArray(data) ? data : []);
        setLoading(false);
        setTimeout(() => doCenter(), 50);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  const gradeLabel = useCallback(
    (item) => (lang === 'fr' ? (item.grade_fr || item.grade) : (item.grade_en || item.grade)),
    [lang]
  );

  const laptopCatalog = useMemo(() => catalog.filter(i => i.kind !== 'smartphone'), [catalog]);
  const phoneCatalog = useMemo(() => catalog.filter(i => i.kind === 'smartphone'), [catalog]);

  const currentCatalog = activeTab === 0 ? laptopCatalog : phoneCatalog;
  const currentFilters = activeTab === 0 ? laptopFilters : phoneFilters;
  const currentFilterFields = activeTab === 0 ? LAPTOP_FILTER_FIELDS : SMARTPHONE_FILTER_FIELDS;
  const currentColumns = activeTab === 0 ? LAPTOP_COLUMNS : SMARTPHONE_COLUMNS;
  const currentVisibleCols = activeTab === 0 ? visibleLaptopCols : visiblePhoneCols;

  const uniqueValues = useMemo(() => {
    const result = {};
    currentFilterFields.forEach(f => {
      const vals = [...new Set(
        currentCatalog.map(item => {
          if (f === 'grade') return gradeLabel(item);
          return item[f];
        }).filter(v => v !== null && v !== undefined && v !== '')
      )];
      vals.sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      });
      result[f] = vals;
    });
    return result;
    // eslint-disable-next-line
  }, [currentCatalog, currentFilterFields, gradeLabel]);

  const filteredCatalog = useMemo(() => {
    let result = currentCatalog.filter(item => {
      if (currentFilters.name && !(item.model || item.name).toLowerCase().includes(currentFilters.name.toLowerCase())) return false;
      if (currentFilters.brand !== 'all' && item.brand !== currentFilters.brand) return false;
      if ((currentFilters.ram || 'all') !== 'all' && String(item.ram) !== currentFilters.ram) return false;
      if ((currentFilters.storage || 'all') !== 'all' && String(item.storage) !== currentFilters.storage) return false;
      if ((currentFilters.grade || 'all') !== 'all' && gradeLabel(item) !== currentFilters.grade) return false;
      if ((currentFilters.color || 'all') !== 'all' && item.color !== currentFilters.color) return false;
      if ((currentFilters.cpu || 'all') !== 'all' && item.cpu !== currentFilters.cpu) return false;
      if ((currentFilters.battery || 'all') !== 'all' && item.battery !== currentFilters.battery) return false;
      if ((currentFilters.gpu || 'all') !== 'all' && item.gpu !== currentFilters.gpu) return false;
      if ((currentFilters.os || 'all') !== 'all' && item.os !== currentFilters.os) return false;
      return true;
    });

    result.sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '').toLowerCase();
      vb = String(vb || '').toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [currentCatalog, currentFilters, sortField, sortDir, gradeLabel]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleSelect = (sku) => {
    setSelected(prev => ({ ...prev, [sku]: !prev[sku] }));
  };

  const toggleSelectAll = () => {
    const allChecked = filteredCatalog.length > 0 && filteredCatalog.every(item => selected[item.sku]);
    const newSel = { ...selected };
    filteredCatalog.forEach(item => { newSel[item.sku] = !allChecked; });
    setSelected(newSel);
  };

  const selectedItems = catalog.filter(item => selected[item.sku]);

  const handleQuoteRequest = () => {
    if (selectedItems.length === 0) {
      alert(t('shopNoSelection', lang));
      return;
    }
    setShowQuote(true);
  };

  const setFilter = (key, value) => {
    if (activeTab === 0) {
      setLaptopFilters(prev => ({ ...prev, [key]: value }));
    } else {
      setPhoneFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const makeFilterOptions = (field) => [
    { value: 'all', label: t('shopFilterAll', lang) },
    ...(uniqueValues[field] || []).map(v => ({
      value: String(v),
      label: field === 'ram' ? v + ' Go' : String(v)
    }))
  ];

  const toggleCol = (field) => {
    if (activeTab === 0) {
      setVisibleLaptopCols(prev => {
        const next = new Set(prev);
        if (next.has(field)) next.delete(field);
        else next.add(field);
        return next;
      });
      return;
    }
    setVisiblePhoneCols(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const activeColumns = currentColumns.filter(c => currentVisibleCols.has(c[0]));

  const renderCell = (item, field) => {
    switch (field) {
      case 'type':
        return (
          <TableDataCell key={field} style={{ textAlign: 'center' }}>
            {item.type === 'apple'
              ? <i className="fa-brands fa-apple"></i>
              : <i className="fa-brands fa-windows"></i>}
          </TableDataCell>
        );
      case 'model':
        return <TableDataCell key={field}>{item.model || item.name}</TableDataCell>;
      case 'ram':
        return <TableDataCell key={field}>{item.ram ? item.ram + ' Go' : ''}</TableDataCell>;
      case 'grade':
        return <TableDataCell key={field}>{gradeLabel(item)}</TableDataCell>;
      case 'price_ttc':
        return (
          <TableDataCell key={field} style={{ whiteSpace: 'nowrap' }}>
            <strong>{item.price_ttc.toFixed(2)} € TTC</strong><br />
            <small>{item.price_ht.toFixed(2)} € HT</small><br />
            <small>{t('shopEcotax', lang)} : {item.ecotax.toFixed(2)} €</small>
          </TableDataCell>
        );
      default:
        return <TableDataCell key={field}>{item[field] || ''}</TableDataCell>;
    }
  };

  const hasActiveFilter = Object.entries(currentFilters).some(([k, v]) => k === 'name' ? v !== '' : v !== 'all');
  const allChecked = filteredCatalog.length > 0 && filteredCatalog.every(item => selected[item.sku]);
  const someChecked = filteredCatalog.some(item => selected[item.sku]);

  const renderFilterRow = (field, labelKey) => (
    <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <span style={{ minWidth: 80, flexShrink: 0 }}>{t(labelKey, lang)}</span>
      <Select
        defaultValue={currentFilters[field] || 'all'}
        options={makeFilterOptions(field)}
        onChange={(opt) => setFilter(field, opt.value)}
        width={180}
        menuMaxHeight={200}
      />
    </div>
  );

  const handleTabChange = (value) => {
    setActiveTab(value);
    setOpenMenu(null);
    setSortField('price_ttc');
    setSortDir('asc');
  };

  return (
    <>
      <Draggable bounds="parent" handle=".draggable-title">
        <Window style={{ opacity, zIndex }} className='window center-window shop-window' onMouseDownCapture={onFocus}>
          <WindowHeader className={`window-title${isFocused && !showQuote ? '' : ' inactive'}`}>
            <div className="float-right">
              <Button onClick={close}>&times;</Button>
            </div>
            <div className="draggable-title">
              <span className="Computer_16x16_4 window-icon"></span>
              <span> {t('shopTitle', lang)}</span>
            </div>
          </WindowHeader>

          <Toolbar style={{ padding: '2px 4px', flexShrink: 0 }} ref={menuRef}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant='menu'
                size='sm'
                active={openMenu === 'filters'}
                onClick={() => setOpenMenu(openMenu === 'filters' ? null : 'filters')}
              >
                {t('shopFilters', lang)}{hasActiveFilter ? ' ●' : ''}
              </Button>
              {openMenu === 'filters' && (
                <div className="shop-dropdown" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 10 }}>
                  <div style={{ padding: 8, minWidth: 300 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                      <span style={{ minWidth: 80, flexShrink: 0 }}>{t('shopModel', lang)}</span>
                      <TextInput
                        value={currentFilters.name}
                        onChange={e => setFilter('name', e.target.value)}
                        placeholder={t('shopModel', lang)}
                        width={180}
                      />
                    </div>
                    {renderFilterRow('brand', 'shopBrand')}
                    {activeTab === 0 && renderFilterRow('ram', 'shopRam')}
                    {renderFilterRow('storage', 'shopStorage')}
                    {renderFilterRow('grade', 'shopGrade')}
                    {renderFilterRow('color', 'shopColor')}
                    {activeTab === 0 && renderFilterRow('cpu', 'shopCpu')}
                    {activeTab === 0 && renderFilterRow('battery', 'shopBattery')}
                    {activeTab === 0 && renderFilterRow('gpu', 'shopGpu')}
                    {activeTab === 0 && renderFilterRow('os', 'shopOs')}
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant='menu'
                size='sm'
                active={openMenu === 'columns'}
                onClick={() => setOpenMenu(openMenu === 'columns' ? null : 'columns')}
              >
                {t('shopColumns', lang)}
              </Button>
              {openMenu === 'columns' && (
                <div className="shop-dropdown" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 10 }}>
                  <div style={{ padding: 8, minWidth: 160 }}>
                    {currentColumns.filter(c => c[1]).map(([field, labelKey]) => (
                      <label key={field} className="shop-col-toggle" style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
                        <Checkbox
                          checked={currentVisibleCols.has(field)}
                          onChange={() => toggleCol(field)}
                        />
                        <span style={{ marginLeft: 4 }}>{t(labelKey, lang)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Separator orientation='vertical' size='35px' />
            <Button
              variant='menu'
              size='sm'
              onClick={handleQuoteRequest}
              disabled={selectedItems.length === 0}
            >
              {t('shopQuoteBtn', lang)} {selectedItems.length > 0 && `(${selectedItems.length})`}
            </Button>
          </Toolbar>

          <WindowContent style={{ padding: 4, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <ProgressBar variant='tile' value={100} />
                <p className="top-8">{t('shopLoading', lang)}</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <p>{t('shopError', lang)}</p>
                <Button onClick={fetchCatalog} className="top-8">{t('shopRetry', lang)}</Button>
              </div>
            ) : (
              <>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab value={0}>{t('shopTabLaptops', lang)}</Tab>
                  <Tab value={1}>{t('shopTabSmartphones', lang)}</Tab>
                </Tabs>
                <TabBody style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div className="overflow-table" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeadCell style={{ width: 30 }}>
                            <Checkbox
                              checked={allChecked}
                              indeterminate={someChecked && !allChecked}
                              onChange={toggleSelectAll}
                            />
                          </TableHeadCell>
                          {activeColumns.map(([field, labelKey]) => (
                            <TableHeadCell
                              key={field}
                              onClick={() => toggleSort(field)}
                              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {labelKey ? t(labelKey, lang) : ''}{sortIndicator(field)}
                            </TableHeadCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCatalog.length === 0 ? (
                          <TableRow>
                            <TableDataCell colSpan={activeColumns.length + 1} style={{ textAlign: 'center' }}>
                              {t('shopEmpty', lang)}
                            </TableDataCell>
                          </TableRow>
                        ) : filteredCatalog.map(item => (
                          <TableRow key={item.sku}>
                            <TableDataCell style={{ textAlign: 'center' }}>
                              <Checkbox
                                checked={!!selected[item.sku]}
                                onChange={() => toggleSelect(item.sku)}
                              />
                            </TableDataCell>
                            {activeColumns.map(([field]) => renderCell(item, field))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabBody>
              </>
            )}
          </WindowContent>
        </Window>
      </Draggable>
      {showQuote && (
        <WindowQuote
          items={selectedItems}
          close={() => setShowQuote(false)}
          centerWindows={centerWindows}
          zIndex={(zIndex || 0) + 1}
          isFocused={true}
          lang={lang}
        />
      )}
    </>
  );
}

export default WindowShop;
